import type { AppLocale, SpeakingHistoryItem } from '@ielts-buddy/shared'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { t } from '../i18n/messages.js'
import { ApiError, fetchSpeakingHistory } from '../shared/api.js'
import { usePreferencesStore } from '../shared/preferences-store.js'
import { useRouter } from '../shared/router-store.js'
import { haptics } from '../shared/telegram.js'
import { ChartBarIcon } from '../shared/ui/icons.js'
import { Button, Card, ErrorState, LoadingDots } from '../shared/ui/index.js'
import { BandProgressChart } from './BandProgressChart.js'

function kindLabel(
  locale: AppLocale,
  kind: SpeakingHistoryItem['kind'],
): string {
  if (kind === 'full_test') return t(locale, 'results.kind.full')
  if (kind === 'warm_up') return t(locale, 'results.kind.warm')
  return t(locale, 'results.kind.section')
}

function userFacingHistoryError(locale: AppLocale, err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 404) return t(locale, 'results.historyNotFound')
    if (err.status === 401) return t(locale, 'app.signInError')
    if (err.status >= 500) return t(locale, 'results.historyServerError')
  }
  return t(locale, 'results.historyGeneric')
}

export function ResultsTabScreen() {
  const locale = usePreferencesStore((s) => s.locale)
  const navigateToSpeakingResults = useRouter(
    (s) => s.navigateToSpeakingResults,
  )
  const navigateHome = useRouter((s) => s.navigateHome)
  const [sessions, setSessions] = useState<SpeakingHistoryItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const res = await fetchSpeakingHistory()
      setSessions(res.sessions)
    } catch (e) {
      setSessions(null)
      setError(userFacingHistoryError(locale, e))
    }
  }, [locale])

  useEffect(() => {
    void load()
  }, [load])

  // Refetch when returning from another screen or the Telegram webview regains focus
  // so a just-finished warm-up / section appears without a full app reload.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') void load()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [load])

  const chartPoints = useMemo(() => {
    if (!sessions) return []
    const scored = sessions.filter(
      (s): s is SpeakingHistoryItem & { overallBand: number } =>
        s.overallBand != null,
    )
    const mapped = scored.map((s) => ({
      completedAt: s.completedAt,
      overallBand: s.overallBand,
    }))
    mapped.sort(
      (a, b) =>
        new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime(),
    )
    return mapped
  }, [sessions])

  if (error) {
    return (
      <div className="tab-screen screen-padding flex flex-col gap-4 animate-fade-in">
        <h1 className="text-xl font-semibold text-brand-text">
          {t(locale, 'results.title')}
        </h1>
        <ErrorState
          title={t(locale, 'results.loadError')}
          message={error}
          primaryAction={{
            label: t(locale, 'results.retry'),
            onClick: () => void load(),
          }}
        />
      </div>
    )
  }

  if (sessions === null) {
    return (
      <div className="tab-screen screen-padding flex flex-col gap-4">
        <h1 className="text-xl font-semibold text-brand-text">
          {t(locale, 'results.title')}
        </h1>
        <LoadingDots label={t(locale, 'common.loading')} />
      </div>
    )
  }

  if (sessions.length === 0) {
    return (
      <div className="tab-screen screen-padding flex flex-col gap-3 animate-fade-in">
        <h1 className="shrink-0 text-xl font-semibold text-brand-text">
          {t(locale, 'results.title')}
        </h1>
        <div className="flex min-h-0 flex-1 flex-col justify-center py-1">
          <Card
            size="default"
            tone="surface"
            className="mx-auto flex min-h-0 w-full max-w-sm flex-col items-center gap-3 overflow-y-auto border border-brand-settings-outline p-4 text-center sm:p-5"
          >
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-brand-primary-soft text-brand-primary-strong"
              aria-hidden
            >
              <ChartBarIcon className="h-6 w-6" />
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <h2 className="text-base font-semibold leading-snug text-brand-text">
                {t(locale, 'results.emptyTitle')}
              </h2>
              <p className="text-sm leading-snug text-brand-text-muted">
                {t(locale, 'results.emptyBody')}
              </p>
            </div>
            <Button
              variant="gradient"
              type="button"
              className="w-full shrink-0"
              onClick={() => {
                haptics.medium()
                navigateHome()
              }}
            >
              {t(locale, 'results.goHome')}
            </Button>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="tab-screen screen-padding flex flex-col gap-4 animate-fade-in pb-2">
      <h1 className="text-xl font-semibold text-brand-text">
        {t(locale, 'results.title')}
      </h1>

      {chartPoints.length > 0 ? (
        <BandProgressChart locale={locale} points={chartPoints} />
      ) : (
        <Card
          size="flat"
          tone="surface"
          className="border border-brand-border px-4 py-3"
        >
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted">
            {t(locale, 'results.progressTitle')}
          </p>
          <p className="mt-1.5 text-sm text-brand-text-muted leading-snug">
            {t(locale, 'results.progressNoScores')}
          </p>
        </Card>
      )}

      <h2 className="text-sm font-semibold text-brand-text">
        {t(locale, 'results.historyHeading')}
      </h2>
      <ul className="flex flex-col gap-2">
        {sessions.map((s) => (
          <li key={s.sessionId}>
            <button
              type="button"
              onClick={() => {
                haptics.light()
                navigateToSpeakingResults(s.sessionId)
              }}
              className="w-full text-left"
            >
              <Card
                size="default"
                tone="surface"
                className="p-4 flex flex-col gap-1 active:scale-[0.99] transition-transform"
              >
                <div className="flex justify-between gap-2 items-baseline">
                  <span className="text-sm font-medium text-brand-text">
                    {kindLabel(locale, s.kind)}
                  </span>
                  {s.overallBand != null && (
                    <span className="text-sm font-semibold text-brand-primary-strong">
                      {t(locale, 'results.band')} {s.overallBand}
                    </span>
                  )}
                </div>
                <span className="text-xs text-brand-text-muted">
                  {new Date(s.completedAt).toLocaleString()}
                </span>
              </Card>
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}
